// Saf BE→FE auth eşleme fonksiyonları.
//
// Bu modül BİLİNÇLİ olarak `./http`'ten hiçbir şey import ETMEZ (yalnız types +
// ROLE sabiti). Böylece `http.ts` (refreshSessionFromCookie) bu eşlemeleri
// kullanabilir ve `auth.ts` ↔ `http.ts` arasındaki döngüsel bağımlılık kırılır.
// `auth.ts` bu fonksiyonları re-export eder (mevcut `@api/auth` import'ları bozulmaz).

import type {
  AdminUser,
  AuthSession,
  AvailableProject,
  BackendAuthResultDto,
  BackendCmsRole,
  BackendProjectSummaryDto,
  BackendUserProfileDto,
} from '@features/auth/types';
import { ROLE, type Role } from '@shared/rbac/permissions.const';

/**
 * ISSUE-59 — Canonical CMS role mapping.
 *
 * The BE now exposes `UserProfileDto.cmsRole` (`ADMIN | EDITOR | USER`),
 * derived server-side via `CmsRoleResolver`. FE used to infer the role
 * heuristically from `roles[]`, which drifted whenever a user held multiple
 * Identity roles. We now read `cmsRole` directly. Only the role array is
 * consulted when `cmsRole` is missing — that path exists purely for
 * back-compat with stale persisted sessions from before the BE change.
 */
function mapCmsRoleToFeRole(cmsRole: string | null | undefined): Role | null {
  if (!cmsRole) return null;
  switch (cmsRole.toUpperCase()) {
    case 'ADMIN':
      return ROLE.ADMIN;
    case 'EDITOR':
      return ROLE.EDITOR;
    case 'USER':
      // FE has no `USER` enum value; treat as the most restrictive Cms role.
      return ROLE.WRITER;
    default:
      return null;
  }
}

/** @deprecated Fallback heuristic — only used when BE omits `cmsRole`. */
function inferRoleFromBackendRoles(roles: readonly string[] | null | undefined): Role {
  const set = new Set((roles ?? []).map((r) => r.toLowerCase()));
  if (set.has('superadmin') || set.has('admin')) return ROLE.ADMIN;
  if (set.has('editor')) return ROLE.EDITOR;
  if (set.has('writer') || set.has('author')) return ROLE.WRITER;
  return ROLE.WRITER;
}

function joinName(
  first: string | null | undefined,
  last: string | null | undefined,
  email: string,
): string {
  const composed = [first, last].filter(Boolean).join(' ').trim();
  return composed.length > 0 ? composed : email;
}

function normalizeCmsRole(raw: string | null | undefined): BackendCmsRole | undefined {
  if (!raw) return undefined;
  const upper = raw.toUpperCase();
  if (upper === 'ADMIN' || upper === 'EDITOR' || upper === 'USER') {
    return upper;
  }
  return undefined;
}

export function mapBackendUser(u: BackendUserProfileDto): AdminUser {
  const email = u.email ?? '';
  const cmsRole = normalizeCmsRole(u.cmsRole);
  const role = mapCmsRoleToFeRole(cmsRole) ?? inferRoleFromBackendRoles(u.roles);
  return {
    id: u.id,
    email,
    fullName: joinName(u.firstName, u.lastName, email),
    role,
    status: 'active',
    avatarUrl: u.avatarUrl ?? undefined,
    createdAt: new Date().toISOString(),
    firstName: u.firstName ?? undefined,
    lastName: u.lastName ?? undefined,
    memberType: u.memberType ?? undefined,
    mustResetPassword: u.mustResetPassword,
    roles: u.roles ?? [],
    permissions: u.permissions ?? [],
    cmsRole,
    twoFactorEnabled: u.twoFactorEnabled ?? undefined,
  };
}

/** REQ-45 — BE proje özetini FE `AvailableProject`'e eşle (garanti-alanlı). */
function mapProjectSummary(p: BackendProjectSummaryDto): AvailableProject {
  return {
    id: p.id,
    name: p.name ?? '',
    slug: p.slug ?? '',
    isDefault: p.isDefault ?? false,
  };
}

export function mapBackendAuthResult(r: BackendAuthResultDto): AuthSession {
  // K-8: gövdedeki refreshToken bilinçli olarak eşlenmez — token httpOnly
  // cookie olarak taşınır, FE tarafında saklanmaz.
  return {
    user: mapBackendUser(r.user),
    accessToken: r.accessToken,
    expiresAt: r.accessTokenExpiresAt,
    // REQ-45 — proje switcher'ı için erişilebilir projeler + varsayılan.
    availableProjects: (r.availableProjects ?? []).map(mapProjectSummary),
    defaultProjectId: r.defaultProjectId ?? null,
  };
}
